import { writeFile } from "node:fs/promises";
import { join } from "node:path";
import { existsSync } from "node:fs";
import { DecisionStore, renderTemplate } from "@vortex-os/decision-log";
import type { Command, CommandInput } from "@vortex-os/slash-commands";

export interface NewDecisionResult {
  readonly path: string;
  readonly date: string;
  readonly slug: string;
}

/**
 * `/decision <slug> <title...>` — Create a new Decision Log entry from
 * the canonical template at `data/decision-log/<today>-<slug>.md`.
 *
 * `slug` is required and used in the filename. The remaining tokens form
 * the entry title (the H1 in the rendered body). The command refuses to
 * overwrite an existing file — it errors out so the caller can decide.
 */
export const decisionCommand: Command<NewDecisionResult> = {
  name: "decision",
  description: "Create a new Decision Log entry from the canonical template at `data/decision-log/<today>-<slug>.md`. Refuses to overwrite an existing file.",
  args: [
    { name: "slug", description: "Kebab-style identifier used in the filename.", required: true },
    { name: "title", description: "One-line decision title (rest of the input).", required: true },
  ],
  handler: async (input: CommandInput): Promise<NewDecisionResult> => {
    const slug = input.args.slug;
    if (!slug) {
      throw new Error("`/decision` requires a slug argument.");
    }
    // The runner already populated args.slug from the first token. The
    // title is `rest` minus that first token; recover it by taking the
    // raw input and stripping the leading "decision <slug>".
    const title = extractTitle(input.rest, slug);
    if (!title) {
      throw new Error("`/decision` requires a title after the slug.");
    }

    const date = todayIso();
    const dir = join(input.context.dataDir, "decision-log");
    const store = new DecisionStore(dir);
    const path = store.pathFor(date, slug);

    if (existsSync(path)) {
      throw new Error(`Refusing to overwrite existing entry: ${path}`);
    }

    const body = renderTemplate({ date, slug, title });
    await writeFile(path, body, "utf8");
    return { path, date, slug };
  },
};

function extractTitle(rest: string, slug: string): string {
  const trimmed = rest.trim();
  if (trimmed.startsWith(slug)) {
    return trimmed.slice(slug.length).trim();
  }
  return trimmed;
}

function todayIso(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
